import { CartItem } from '../models/cart';
import { PaymentMethod, PaymentSettings } from '../models';

export interface InstallmentSettings {
  maxInstallments: number;
  minInstallmentValue: number;
  installmentsWithoutInterest: boolean;
}

export interface InstallmentInfo {
  count: number;
  amount: number;
}

export interface CheckoutTotals {
  gross: number;
  pixDiscount: number;
  net: number;
  pixDiscountApplies: boolean;
}

const CENTS_PER_UNIT = 100;
const PERCENT_DIVISOR = 100;

const toCents = (value: number) => Math.round(value * CENTS_PER_UNIT);

export function getPixPrice(price: number, pixDiscountPercentage: number): number | null {
  if (pixDiscountPercentage <= 0) return null;
  return price * (1 - pixDiscountPercentage / 100);
}

export function getCartPixPrice(
  items: CartItem[],
  total: number,
  pixDiscountPercentage: number
): number | null {
  const promoTotal = items.reduce(
    (sum, i) => (i.promotionalPrice != null ? sum + i.promotionalPrice * i.quantity : sum),
    0
  );
  const eligibleTotal = total - promoTotal;
  if (eligibleTotal <= 0) return null;
  const discounted = getPixPrice(eligibleTotal, pixDiscountPercentage);
  return discounted !== null ? discounted + promoTotal : null;
}

export function getCheckoutTotals(
  items: CartItem[],
  paymentEntries: ReadonlyArray<{ paymentMethod: PaymentMethod }>,
  settings: Pick<PaymentSettings, 'pixDiscountPercentage'> | null
): CheckoutTotals {
  const grossCents = items.reduce(
    (sum, i) => sum + toCents((i.promotionalPrice ?? i.price) * i.quantity),
    0
  );
  const eligibleCents = items.reduce(
    (sum, i) => (i.promotionalPrice == null ? sum + toCents(i.price * i.quantity) : sum),
    0
  );
  const percentage = settings?.pixDiscountPercentage ?? 0;
  const allPix =
    paymentEntries.length > 0 && paymentEntries.every((e) => e.paymentMethod === PaymentMethod.Pix);
  const discountCents =
    allPix && percentage > 0 ? Math.round((eligibleCents * percentage) / PERCENT_DIVISOR) : 0;

  return {
    gross: grossCents / CENTS_PER_UNIT,
    pixDiscount: discountCents / CENTS_PER_UNIT,
    net: (grossCents - discountCents) / CENTS_PER_UNIT,
    pixDiscountApplies: discountCents > 0,
  };
}

export function getInstallmentInfo(price: number, settings: InstallmentSettings): InstallmentInfo | null {
  if (settings.minInstallmentValue <= 0) return null;
  for (let count = settings.maxInstallments; count >= 2; count--) {
    const amount = price / count;
    if (amount >= settings.minInstallmentValue) {
      return { count, amount };
    }
  }
  return null;
}
